import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterTripInfo } from '../../../api/centers/get-center-trip-info'
import { getTripTripbookings } from '../../../api/trips/get-trip-trip-bookings'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { CenterTripInfoKeys, TripBasic } from '../../../lib/types/models/trip/trip'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripBookingDetails } from './center-trip-bookings.ui'
import { CenterTripFiltersType } from './center-trips-filter'

const CENTER_TRIP_INFO_KEYS: CenterTripInfoKeys[] = [
  'numberOfStudentsBlocked',
  'numberOfStudentsAffected',
  'numberOfAssignedStaffs',
  'numberOfStaffPlacesRequested',
  'numberOfStudentsPlacesRequested'
] as const

type TripTripBookingsAccordionProps = {
  centerId: number
  trip: TripBasic
  filters: CenterTripFiltersType
  enabled?: boolean
}

export function TripTripBookingsAccordion({
  centerId,
  trip,
  filters,
  enabled
}: TripTripBookingsAccordionProps) {
  const [tripBookingsAccordion, setTripBookingsAccordion] = useState<Record<number, boolean>>()
  const [tripBookings, setTripBookings] = useState<TripBookingTypes.Light[]>([])

  const tripId = trip.id

  const { data: tripInfo, isPending } = useQuery({
    queryKey: ['center-trip-info', centerId, tripId, filters.directionalities],
    queryFn: () =>
      getCenterTripInfo(centerId, tripId, CENTER_TRIP_INFO_KEYS, {
        directionalities: filters.directionalities
      }),
    enabled: enabled && trip.type === TripTypeEnum.PRIMARY
  })

  const { isPending: isTripBookingsPending } = useQuery({
    queryKey: ['center-trip-bookings', centerId, tripId, filters.directionalities],
    queryFn: async () => {
      const res = await getTripTripbookings(tripId, {
        filter: { directionalities: filters.directionalities, centerId }
      })

      setTripBookings(res.data)
    },
    enabled
  })

  const {
    numberOfStudentsPlacesRequested,
    numberOfStudentsBlocked,
    numberOfStudentsAffected,
    numberOfStaffPlacesRequested,
    numberOfAssignedStaffs
  } = tripInfo ?? {}

  return (
    <div className={'flex flex-col gap-3'}>
      {tripInfo && (
        <div className={'flex flex-row gap-2'}>
          {isPending ? (
            <>
              <Skeleton className={'w-24 h-7 rounded-sm'} />
              <Skeleton className={'w-24 h-7 rounded-sm'} />
            </>
          ) : (
            <>
              <Badge
                text={`# max de jeunes: ${numberOfStudentsPlacesRequested} | # jeunes bloqués: ${numberOfStudentsBlocked} | # jeunes affectés: ${numberOfStudentsAffected}`}
                variant={'brown'}
              />
              <Badge
                text={`# encadrants prévus: ${numberOfStaffPlacesRequested} | # encadrants affectés: ${numberOfAssignedStaffs}`}
                variant={'blue'}
              />
            </>
          )}
        </div>
      )}

      <div className={'flex flex-col gap-2'}>
        {isTripBookingsPending ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className={'w-full h-20'} />
          ))
        ) : tripBookings.length === 0 ? (
          <Typography variant={'tag'}>Aucune réservation</Typography>
        ) : (
          tripBookings.map((tripBooking, index) => {
            const isTripBookingOpen = tripBookingsAccordion?.[tripBooking.id] ?? false

            const transportMeans = tripBooking.relatedTrip.transportMeans
            const transportMeansLabel = transportMeans
              ? ` - ${TripProperties.handleTransportMeansName(transportMeans)}`
              : ''

            return (
              <Accordion
                key={`${tripBooking.id}-tripbooking`}
                variant={'primary'}
                className={'bg-white-hover rounded-lg'}
                toggleRounded
                open={isTripBookingOpen}
                onToggle={() =>
                  setTripBookingsAccordion((prev) => ({
                    ...prev,
                    [tripBooking.id]: !prev?.[tripBooking.id]
                  }))
                }
                title={
                  <Typography
                    variant={'tag'}
                    color={tailwindColors.primary.DEFAULT}
                  >{`Réservation (${index + 1})${transportMeansLabel}`}</Typography>
                }
              >
                <TripBookingDetails
                  centerId={centerId}
                  tripId={tripId}
                  tripBooking={tripBooking}
                  enabled={isTripBookingOpen}
                />
              </Accordion>
            )
          })
        )}
      </div>
    </div>
  )
}
